import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { adminFetchMenu, adminFetchEvents, adminFetchGallery, adminFetchInquiries } from "@/lib/api";
import { UtensilsCrossed, Calendar, Image, Inbox } from "lucide-react";

function Stat({ icon: Icon, label, value, to }) {
    return (
        <Link to={to} className="surface-card rounded-xl p-5 flex items-center justify-between hover:border-orange-400/40" data-testid={`stat-${label.toLowerCase()}`}>
            <div>
                <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90">{label}</div>
                <div className="font-display text-3xl mt-1 text-white">{value}</div>
            </div>
            <div className="p-2 rounded-md bg-[rgba(255,140,0,0.12)] border border-[rgba(255,140,0,0.35)] text-orange-300"><Icon size={20} /></div>
        </Link>
    );
}

export default function AdminDashboardPage() {
    const [counts, setCounts] = useState({ menu: 0, events: 0, gallery: 0, inquiries: 0, newInquiries: 0 });

    useEffect(() => {
        Promise.all([
            adminFetchMenu().catch(() => []),
            adminFetchEvents().catch(() => []),
            adminFetchGallery().catch(() => []),
            adminFetchInquiries().catch(() => []),
        ]).then(([menu, events, gallery, inquiries]) => {
            setCounts({
                menu: menu.length,
                events: events.length,
                gallery: gallery.length,
                inquiries: inquiries.length,
                newInquiries: inquiries.filter((i) => i.status === "new").length,
            });
        });
    }, []);

    return (
        <div data-testid="admin-dashboard-page">
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90">Admin</div>
                <h1 className="font-display text-3xl sm:text-4xl tracking-[0.05em] text-white">Pit Control</h1>
                <p className="text-white/70 mt-1">Manage menu, events, gallery and bookings.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={UtensilsCrossed} label="Menu Items" value={counts.menu} to="/admin/menu" />
                <Stat icon={Calendar} label="Events" value={counts.events} to="/admin/events" />
                <Stat icon={Image} label="Gallery" value={counts.gallery} to="/admin/gallery" />
                <Stat icon={Inbox} label={`Inquiries (${counts.newInquiries} New)`} value={counts.inquiries} to="/admin/inquiries" />
            </div>
            <Card className="surface-card border-0 mt-6">
                <CardContent className="p-6">
                    <div className="font-display text-xl tracking-[0.05em] mb-2">Quick Tips</div>
                    <ul className="text-white/75 text-sm space-y-1 list-disc list-inside">
                        <li>Edit prices and descriptions on the Menu page — changes go live instantly.</li>
                        <li>Toggle event visibility between <span className="text-orange-300">public</span> and <span className="text-orange-300">private</span>; private events appear only as &ldquo;Private Event&rdquo; on the public calendar.</li>
                        <li>Paste a Google Calendar embed URL in <Link className="text-orange-300" to="/admin/settings">Settings</Link> to display a live calendar.</li>
                    </ul>
                </CardContent>
            </Card>
        </div>
    );
}
